/**
 * Typed copy for the /wheel-of-life/template printable landing page, plus the
 * JSON-LD builder derived from the same data. Single source of truth so the
 * visible content and the structured data cannot drift.
 *
 * Aliases: wheel of life template, printable wheel of life, life balance wheel,
 * wheel of life worksheet, wheel of life pdf.
 */

export const TEMPLATE_CANONICAL =
  "https://dreamcoachmatch.com/wheel-of-life/template";

export const TEMPLATE_PDF_LABELED = "/downloads/wheel-of-life-template.pdf";
export const TEMPLATE_PDF_BLANK = "/downloads/wheel-of-life-template-blank.pdf";

export const TEMPLATE_BLUF =
  "The Wheel of Life template is a free printable worksheet that maps your satisfaction across eight life areas — Career, Money, Health, Family, Romance, Friends, Personal Growth, and Fun. You score each from 1 to 10, plot the points around a circle, and connect them into a shape. The shape is the insight: a round wheel means life feels even, a lopsided one shows exactly which areas are dragging. It takes about five minutes and needs no training, which is why coaches use it to open a first session and individuals use it to take an honest read on their own. Download the printable PDF worksheet above to fill it in by hand, or use the Wheel of Life assessment from Dream Coach Match to score on screen and get an instant reflection. Either way, the Coaching Intelligence Hub designed this template to turn a vague sense of imbalance into a clear, specific picture you can act on.";

export type TitledBlock = { title: string; body: string };
export type Faq = { q: string; a: string };
export type ScoreLegendItem = { score: number; label: string };

export const TEMPLATE_HOWTO_STEPS: readonly TitledBlock[] = [
  {
    title: "Choose your 8–10 life areas",
    body: "Start with the eight defaults — Career, Money, Health, Family, Romance, Friends, Personal Growth, and Fun. Swap or add areas so the wheel reflects your actual life rather than a generic one.",
  },
  {
    title: "Rate your current satisfaction 1–10",
    body: "For each spoke, mark how satisfied you feel today, not how good it looks from outside. Score honestly; your wheel does not need to look round.",
  },
  {
    title: "Connect the dots and read the shape",
    body: "Join your scores into a single outline. A flat tire means one area is dragging the rest, a spiky wheel signals an unaddressed trade-off, and a round but middling wheel often means you are coasting across the board.",
  },
  {
    title: "Mark your desired scores and pick the biggest gap",
    body: "On each spoke, mark where you want to be. The widest gap between current and desired is where your attention is owed. Name one concrete action you could take this week to close it.",
  },
] as const;

export const TEMPLATE_CUSTOMIZING_ITEMS: readonly TitledBlock[] = [
  {
    title: "Swap the categories for a client",
    body: "Rename or replace spokes to fit the person in front of you — Spirituality, Parenting, Business, Recovery, Creativity. A wheel built from a client's own language surfaces more than a generic one. Print a blank PDF, relabel the areas together, and the customizing itself becomes the first coaching conversation.",
  },
  {
    title: "Use it in a first session",
    body: "Most coaches run the wheel in the opening fifteen minutes of an engagement. It gives both of you a shared map: where the client stands across every domain, and which area to start with. It also reveals when the stated problem is downstream of a different, neglected one.",
  },
  {
    title: "Re-run it quarterly to track progress",
    body: "Score the same wheel every one to three months. Comparing the shapes shows movement you would otherwise miss, celebrates real gains, and catches new imbalances early. Dream Coach Match is where coaches and clients connect to do exactly this kind of ongoing work.",
  },
] as const;

export const TEMPLATE_FORMAT_BLURBS: readonly TitledBlock[] = [
  {
    title: "Printable PDF worksheet",
    body: "A free, blank, one-page download in two versions — areas pre-labeled, or fully blank to write your own. Print it, fill it in offline, keep it in a journal, or hand copies to a group.",
  },
  {
    title: "Interactive version",
    body: "Score on screen and get an instant, personalized read of your results. Best when you want the wheel drawn for you and the categories easy to edit.",
  },
] as const;

export const TEMPLATE_SCORE_LEGEND: readonly ScoreLegendItem[] = [
  { score: 1, label: "Struggling" },
  { score: 4, label: "Getting by" },
  { score: 7, label: "Going well" },
  { score: 10, label: "Thriving" },
] as const;

/** Illustrative scores for the static body diagram (not a real assessment). */
export const TEMPLATE_SAMPLE_SCORES: readonly number[] = [
  8, 6, 7, 5, 4, 7, 9, 6,
] as const;

export const TEMPLATE_FAQS: readonly Faq[] = [
  {
    q: "Is the Wheel of Life template free?",
    a: "Yes. The Wheel of Life template is completely free and needs no signup or email. Download the printable PDF worksheet or use the Wheel of Life assessment, both at no cost, from Dream Coach Match.",
  },
  {
    q: "Can I print the Wheel of Life?",
    a: "Yes. The PDF is designed to print cleanly on a single page, so you can fill it in by hand, keep it in a journal, or hand copies out in a workshop or group session.",
  },
  {
    q: "How many life areas should the wheel have?",
    a: "Eight is the standard, and 8–10 is the typical range. The template ships with eight defaults — Career, Money, Health, Family, Romance, Friends, Personal Growth, and Fun — and you can add or remove spokes to fit your life.",
  },
  {
    q: "Can I edit the categories?",
    a: "Yes. The categories are meant to be customized. On the printable PDF you simply relabel the spokes by hand, and the Wheel of Life assessment lets you rename and swap areas directly on screen.",
  },
  {
    q: "Is the Wheel of Life the same as the Life Balance Wheel?",
    a: "Yes. The Wheel of Life is also known as the Life Balance Wheel, the Life Wheel, or the Coaching Wheel. They all describe the same tool — a circle of life areas you rate for satisfaction — so this template works whichever name you searched for.",
  },
  {
    q: "What's the difference between the template and the interactive assessment?",
    a: "The template is a blank wheel you fill in yourself, on paper or on screen. The interactive Wheel of Life assessment scores your areas for you, draws the shape automatically, and gives you a personalized reflection on your results.",
  },
  {
    q: "Who created the Wheel of Life?",
    a: "Paul J. Meyer, founder of the Success Motivation Institute, popularized the modern Wheel of Life in the 1960s.",
  },
] as const;

/**
 * Builds the JSON-LD @graph for the template page from the typed copy above.
 * Reuses the site-wide Organization @id so the entity merges (do not emit a
 * second fuller Organization node).
 */
export function buildWheelTemplateJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://dreamcoachmatch.com/#organization",
        name: "Dream Coach Match",
        url: "https://dreamcoachmatch.com/",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://dreamcoachmatch.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Wheel of Life",
            item: "https://dreamcoachmatch.com/wheel-of-life",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Printable template",
            item: TEMPLATE_CANONICAL,
          },
        ],
      },
      {
        "@type": "HowTo",
        name: "How to use the Wheel of Life template",
        description:
          "Score your satisfaction across eight life areas, read the shape of your wheel, and pick the biggest gap to work on.",
        totalTime: "PT5M",
        publisher: { "@id": "https://dreamcoachmatch.com/#organization" },
        step: TEMPLATE_HOWTO_STEPS.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.body,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: TEMPLATE_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}
